export interface TimezoneOption {
  label: string;
  value: string;
  utcOffset: string;
}

/** Curated IANA time zones and city names, matching the backoffice selector. */
const TIMEZONE_DEFINITIONS: Array<{ value: string; cities: string }> = [
  { value: 'Pacific/Pago_Pago', cities: 'American Samoa' },
  { value: 'Pacific/Honolulu', cities: 'Hawaii' },
  { value: 'America/Anchorage', cities: 'Alaska' },
  { value: 'America/Los_Angeles', cities: 'Los Angeles, San Francisco, Seattle' },
  { value: 'America/Tijuana', cities: 'Tijuana' },
  { value: 'America/Denver', cities: 'Denver, Phoenix, Calgary' },
  { value: 'America/Phoenix', cities: 'Phoenix (No DST)' },
  { value: 'America/Chicago', cities: 'Chicago, Houston, Mexico City' },
  { value: 'America/Mexico_City', cities: 'Mexico City, Monterrey' },
  { value: 'America/New_York', cities: 'New York, Toronto, Miami' },
  { value: 'America/Bogota', cities: 'Bogotá, Lima, Quito' },
  { value: 'America/Caracas', cities: 'Caracas' },
  { value: 'America/Halifax', cities: 'Halifax, Atlantic Time' },
  { value: 'America/Santiago', cities: 'Santiago' },
  { value: 'America/St_Johns', cities: 'Newfoundland' },
  { value: 'America/Sao_Paulo', cities: 'São Paulo, Brasília' },
  { value: 'America/Argentina/Buenos_Aires', cities: 'Buenos Aires' },
  { value: 'America/Montevideo', cities: 'Montevideo' },
  { value: 'Atlantic/South_Georgia', cities: 'South Georgia' },
  { value: 'Atlantic/Azores', cities: 'Azores' },
  { value: 'Atlantic/Cape_Verde', cities: 'Cape Verde' },
  { value: 'UTC', cities: 'UTC' },
  { value: 'Europe/London', cities: 'London, Dublin, Lisbon' },
  { value: 'Africa/Casablanca', cities: 'Casablanca' },
  { value: 'Europe/Berlin', cities: 'Berlin, Paris, Madrid, Rome' },
  { value: 'Europe/Amsterdam', cities: 'Amsterdam, Brussels, Copenhagen' },
  { value: 'Europe/Vienna', cities: 'Vienna, Prague, Budapest' },
  { value: 'Europe/Warsaw', cities: 'Warsaw, Stockholm' },
  { value: 'Africa/Lagos', cities: 'Lagos, West Africa' },
  { value: 'Europe/Athens', cities: 'Athens, Bucharest, Istanbul' },
  { value: 'Europe/Helsinki', cities: 'Helsinki, Kyiv, Riga' },
  { value: 'Asia/Jerusalem', cities: 'Jerusalem' },
  { value: 'Africa/Cairo', cities: 'Cairo' },
  { value: 'Africa/Johannesburg', cities: 'Johannesburg, Pretoria' },
  { value: 'Europe/Moscow', cities: 'Moscow, St. Petersburg' },
  { value: 'Asia/Baghdad', cities: 'Baghdad, Kuwait, Riyadh' },
  { value: 'Asia/Riyadh', cities: 'Riyadh' },
  { value: 'Africa/Nairobi', cities: 'Nairobi, East Africa' },
  { value: 'Asia/Tehran', cities: 'Tehran' },
  { value: 'Asia/Dubai', cities: 'Dubai, Abu Dhabi, Muscat' },
  { value: 'Asia/Baku', cities: 'Baku, Tbilisi, Yerevan' },
  { value: 'Asia/Kabul', cities: 'Kabul' },
  { value: 'Asia/Karachi', cities: 'Islamabad, Karachi, Tashkent' },
  { value: 'Asia/Yekaterinburg', cities: 'Yekaterinburg' },
  { value: 'Asia/Kolkata', cities: 'Mumbai, Delhi, Kolkata, Chennai' },
  { value: 'Asia/Kathmandu', cities: 'Kathmandu' },
  { value: 'Asia/Dhaka', cities: 'Dhaka, Almaty' },
  { value: 'Asia/Yangon', cities: 'Yangon, Myanmar' },
  { value: 'Asia/Bangkok', cities: 'Bangkok, Jakarta, Hanoi' },
  { value: 'Asia/Shanghai', cities: 'Beijing, Shanghai, Hong Kong' },
  { value: 'Asia/Hong_Kong', cities: 'Hong Kong' },
  { value: 'Asia/Singapore', cities: 'Singapore, Kuala Lumpur' },
  { value: 'Australia/Perth', cities: 'Perth' },
  { value: 'Asia/Taipei', cities: 'Taipei' },
  { value: 'Asia/Tokyo', cities: 'Tokyo, Osaka, Sapporo' },
  { value: 'Asia/Seoul', cities: 'Seoul' },
  { value: 'Australia/Adelaide', cities: 'Adelaide' },
  { value: 'Australia/Darwin', cities: 'Darwin' },
  { value: 'Australia/Sydney', cities: 'Sydney, Melbourne, Canberra' },
  { value: 'Australia/Brisbane', cities: 'Brisbane' },
  { value: 'Pacific/Guam', cities: 'Guam, Port Moresby' },
  { value: 'Pacific/Guadalcanal', cities: 'Solomon Islands' },
  { value: 'Pacific/Auckland', cities: 'Auckland, Wellington' },
  { value: 'Pacific/Fiji', cities: 'Fiji' },
  { value: 'Pacific/Tongatapu', cities: "Nuku'alofa, Tonga" },
  { value: 'Pacific/Apia', cities: 'Samoa' },
  { value: 'Pacific/Kiritimati', cities: 'Kiritimati' },
];

/** Current UTC offset, including daylight saving time. */
function getTimezoneOffset(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });

    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((part) => part.type === 'timeZoneName');

    if (offsetPart?.value) {
      return offsetPart.value.replace('GMT', 'UTC');
    }

    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60000;
    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
    const minutes = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';

    return `UTC${sign}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''}`;
  } catch {
    return 'UTC';
  }
}

function generateTimezoneOptions(): TimezoneOption[] {
  const options = TIMEZONE_DEFINITIONS.map((tz) => {
    const utcOffset = getTimezoneOffset(tz.value);
    return {
      label: `${utcOffset} (${tz.cities})`,
      value: tz.value,
      utcOffset,
    };
  });

  return options.sort((a, b) => {
    const getOffsetMinutes = (offset: string): number => {
      if (offset === 'UTC') return 0;
      const sign = offset[3] === '+' ? 1 : -1;
      const [hours, minutes = 0] = offset.slice(4).split(':').map(Number);
      return sign * (hours * 60 + minutes);
    };

    return getOffsetMinutes(a.utcOffset) - getOffsetMinutes(b.utcOffset);
  });
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = generateTimezoneOptions();

export function getTimezoneLabel(timezoneValue: string): string {
  const timezone = TIMEZONE_OPTIONS.find((tz) => tz.value === timezoneValue);
  if (timezone) {
    return timezone.label;
  }

  const offset = getTimezoneOffset(timezoneValue);
  return `${offset} (${timezoneValue})`;
}
